import React from "react";
import { Area, Bath, Bed, Pricetag } from "@/components/bnp-icons";
import MediaPreview from "@/components/bnp-ui/image";
import { formatNumber } from "@/utils/formatNumber";
import { PartnerProperty } from "@/types/partner";
import {
  formatPartnerInfo,
  generatePartnerBadges,
} from "@/utils/partnerBranding";

interface RALDRealtyCardProps {
  property: PartnerProperty;
  onClick?: () => void;
  currentSlide?: number;
  totalSlides?: number;
}

const RALDRealtyCard: React.FC<RALDRealtyCardProps> = ({
  property,
  onClick,
  currentSlide = 0,
  totalSlides = 1,
}) => {
  const partnerInfo = property.partner
    ? formatPartnerInfo(property.partner)
    : null;
  const badges = generatePartnerBadges(property);
  const colors = partnerInfo?.colors || {
    primary: "#1a4d2e",
    secondary: "#2d6a4f",
    text: "#ffffff",
    accent: "#52b788",
  };

  const imageUrl = property.image_url?.[0] || "/assets/images/property.png";

  return (
    <div
      className="relative font-sans text-base w-full sm:w-[350px] lg:max-w-[350px] min-h-[541px] bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer transition-transform hover:scale-105"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onClick?.();
        }
      }}
    >
      {/* Header Badges */}
      <div className="absolute z-10 flex justify-between p-4 w-full">
        <div
          className="flex justify-center font-bold items-center gap-2 text-white p-2 text-sm min-w-[100px] min-h-[44px] sm:min-h-[28px] rounded-lg shadow-md"
          style={{ backgroundColor: colors.primary }}
        >
          <Pricetag />
          <span className="font-bold">
            {property.listing_type || "For Sale"}
          </span>
        </div>
        {property.featured && (
          <div
            className="flex justify-center items-center gap-2 p-2 text-sm text-white rounded-lg min-w-[100px] min-h-[44px] sm:min-h-[28px] shadow-md"
            style={{ backgroundColor: colors.accent }}
          >
            <span className="font-bold">Featured</span>
          </div>
        )}
      </div>

      {/* Partner Logo Overlay */}
      {partnerInfo && (
        <div className="absolute z-10 top-4 right-4 bg-white p-2 rounded-lg shadow-md">
          <img
            src={partnerInfo.logo}
            alt={partnerInfo.displayName}
            className="h-8 w-auto object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = "none";
            }}
          />
        </div>
      )}

      {/* Property Image */}
      <MediaPreview
        src={imageUrl}
        alt={property.address || "Property"}
        className="w-full h-[200px] sm:h-[220px] object-cover"
      />

      {/* Property Details */}
      <div className="relative -mt-5 w-[95%] ml-auto mr-auto">
        <div className="bg-white rounded-t-xl">
          {/* Image Indicators */}
          {totalSlides > 1 && (
            <div className="px-2 py-2 flex gap-2">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-2 rounded-full ${
                    index === currentSlide ? "bg-main-900" : "bg-gray-300"
                  }`}
                  style={
                    index === currentSlide
                      ? { backgroundColor: colors.primary }
                      : undefined
                  }
                />
              ))}
            </div>
          )}

          <div className="py-2 px-3">
            {/* Property Stats */}
            {(property.bedroom_count || property.number_of_plots) && (
              <div className="w-full flex items-center text-sm font-bold gap-2 sm:gap-4 text-soft-400 mb-4 border-b border-soft-200 pb-2">
                {property.bedroom_count && (
                  <>
                    <div className="flex items-center gap-2">
                      <Bed />
                      <span>{property.bedroom_count}</span>
                    </div>
                    <div className="border-r border-grey-5 h-4"></div>
                  </>
                )}
                {property.number_of_plots && (
                  <>
                    <div className="flex items-center gap-2">
                      <Area />
                      <span>{property.number_of_plots} plots</span>
                    </div>
                    <div className="border-r border-grey-5 h-4"></div>
                  </>
                )}
                <div className="flex items-center gap-2">
                  <Bath />
                  <span>Modern</span>
                </div>
              </div>
            )}

            {/* Property Title */}
            <h2 className="text-base sm:text-lg font-bold text-dark-blue mb-4">
              {property.address || "Premium Property"}
            </h2>

            {/* Partner Badges */}
            {badges.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {badges.slice(0, 3).map((badge, index) => (
                  <span
                    key={index}
                    className="text-xs font-semibold px-2 py-1 rounded-full text-white"
                    style={{ backgroundColor: colors.secondary }}
                  >
                    {badge}
                  </span>
                ))}
              </div>
            )}

            {/* Price and Location */}
            <div className="flex flex-col gap-2 mb-4">
              <div>
                <div className="text-grey-4 text-sm">Total package</div>
                <div
                  className="text-lg sm:text-xl font-bold"
                  style={{ color: colors.primary }}
                >
                  ₦{formatNumber(property.total_package || property.price)}
                </div>
              </div>
              {property.city && (
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">Location:</span>{" "}
                  {property.city}
                  {property.state && `, ${property.state}`}
                </div>
              )}
            </div>

            {/* Property Type and Status */}
            <div className="flex justify-between items-center text-sm text-gray-600 border-t border-soft-200 pt-2">
              <span>{property.property_type || "Property"}</span>
              {property.status && (
                <span
                  className="font-semibold"
                  style={{ color: colors.accent }}
                >
                  {property.status}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RALDRealtyCard;
